import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Trash2, CreditCard, Banknote, Building2, Smartphone,
  CheckCircle2, AlertCircle
} from 'lucide-react';

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'text-green-600', bg: 'bg-green-50' },
  { id: 'card', label: 'Card', icon: CreditCard, color: 'text-blue-600', bg: 'bg-blue-50' },
  { id: 'upi', label: 'UPI', icon: Smartphone, color: 'text-purple-600', bg: 'bg-purple-50' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2, color: 'text-orange-600', bg: 'bg-orange-50' },
];

const CARD_TYPES = ['Visa', 'MasterCard', 'Amex', 'RuPay', 'Other'];

export default function MultiPaymentInput({ 
  totalAmount = 0, 
  payments = [], 
  onPaymentsChange,
  disabled = false 
}) {
  const [newPaymentMethod, setNewPaymentMethod] = useState('cash');
  const idCounterRef = useRef(0);

  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const remaining = totalAmount - totalPaid;
  const isBalanced = Math.abs(remaining) < 0.01;

  const addPayment = () => {
    idCounterRef.current += 1;
    const defaultAmount = remaining > 0 ? remaining : 0;
    const newPayment = {
      id: `payment-${idCounterRef.current}`,
      method: newPaymentMethod,
      amount: defaultAmount.toFixed(2),
      // Transaction details based on method
      transaction_id: '',
      reference: '',
      // Card specific
      card_last_four: '',
      card_type: '',
      // UPI specific
      upi_id: '',
      // Bank transfer specific
      bank_name: '',
      bank_reference: ''
    };
    onPaymentsChange([...payments, newPayment]);
  };

  const updatePayment = (id, field, value) => {
    onPaymentsChange(
      payments.map(p => p.id === id ? { ...p, [field]: value } : p)
    );
  };

  const removePayment = (id) => {
    onPaymentsChange(payments.filter(p => p.id !== id));
  };

  const getMethodInfo = (methodId) => {
    return PAYMENT_METHODS.find(m => m.id === methodId) || PAYMENT_METHODS[0];
  };

  const renderTransactionFields = (payment) => {
    const method = payment.method;

    switch (method) {
      case 'card':
        return (
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div>
              <Label className="text-xs text-gray-500">Card Type</Label>
              <Select 
                value={payment.card_type} 
                onValueChange={(v) => updatePayment(payment.id, 'card_type', v)}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {CARD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Last 4 Digits</Label>
              <Input
                value={payment.card_last_four}
                onChange={(e) => updatePayment(payment.id, 'card_last_four', e.target.value.slice(0, 4))}
                placeholder="1234"
                className="h-8 text-xs"
                maxLength={4}
                disabled={disabled}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Transaction ID</Label>
              <Input
                value={payment.transaction_id}
                onChange={(e) => updatePayment(payment.id, 'transaction_id', e.target.value)}
                placeholder="TXN123456"
                className="h-8 text-xs"
                disabled={disabled}
              />
            </div>
          </div>
        );

      case 'upi':
        return (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Label className="text-xs text-gray-500">UPI ID</Label>
              <Input
                value={payment.upi_id}
                onChange={(e) => updatePayment(payment.id, 'upi_id', e.target.value)}
                placeholder="name@upi"
                className="h-8 text-xs"
                disabled={disabled}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Transaction Ref</Label>
              <Input
                value={payment.transaction_id}
                onChange={(e) => updatePayment(payment.id, 'transaction_id', e.target.value)}
                placeholder="123456789012"
                className="h-8 text-xs"
                disabled={disabled}
              />
            </div>
          </div>
        );

      case 'bank_transfer':
        return (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Label className="text-xs text-gray-500">Bank Name</Label>
              <Input
                value={payment.bank_name}
                onChange={(e) => updatePayment(payment.id, 'bank_name', e.target.value)}
                placeholder="HDFC Bank"
                className="h-8 text-xs"
                disabled={disabled}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Reference Number</Label>
              <Input
                value={payment.bank_reference}
                onChange={(e) => updatePayment(payment.id, 'bank_reference', e.target.value)}
                placeholder="NEFT/IMPS Ref"
                className="h-8 text-xs"
                disabled={disabled}
              />
            </div>
          </div>
        );

      case 'cash':
      default:
        return null; // Cash doesn't need transaction details
    }
  };

  return (
    <div className="space-y-3">
      {/* Header with total */}
      <div className="flex items-center justify-between">
        <Label className="text-gray-700 font-medium">Payment Methods</Label>
        <div className="flex items-center gap-2">
          {isBalanced ? (
            <Badge className="bg-green-100 text-green-700 border-green-300">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Balanced
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300">
              <AlertCircle className="h-3 w-3 mr-1" />
              {remaining > 0 ? `₹${remaining.toFixed(0)} remaining` : `₹${Math.abs(remaining).toFixed(0)} excess`}
            </Badge>
          )}
        </div>
      </div>

      {/* Payment entries */}
      <div className="space-y-2">
        {payments.map((payment) => {
          const methodInfo = getMethodInfo(payment.method);
          const Icon = methodInfo.icon;

          return (
            <Card key={payment.id} className={`${methodInfo.bg} border`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  {/* Method icon */}
                  <div className={`p-2 rounded-lg bg-white ${methodInfo.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>

                  {/* Payment details */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-gray-800">{methodInfo.label}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePayment(payment.id)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        disabled={disabled}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Amount input */}
                    <div className="mt-1">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">₹</span>
                        <Input
                          type="number"
                          value={payment.amount}
                          onChange={(e) => updatePayment(payment.id, 'amount', e.target.value)}
                          className="h-8 text-lg font-semibold w-32"
                          min="0"
                          step="0.01"
                          disabled={disabled}
                        />
                      </div>
                    </div>

                    {/* Transaction details based on method */}
                    {renderTransactionFields(payment)}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Add new payment */}
        {!disabled && (
          <div className="flex items-center gap-2 p-2 border-2 border-dashed border-gray-200 rounded-lg">
            <Select value={newPaymentMethod} onValueChange={setNewPaymentMethod}>
              <SelectTrigger className="w-40 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(method => {
                  const Icon = method.icon;
                  return (
                    <SelectItem key={method.id} value={method.id}>
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${method.color}`} />
                        {method.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={addPayment}
              className="flex-1"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Payment
            </Button>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Amount Due</span>
          <span className="font-semibold">₹{totalAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Payments</span>
          <span className={`font-semibold ${isBalanced ? 'text-green-600' : 'text-amber-600'}`}>
            ₹{totalPaid.toFixed(2)}
          </span>
        </div>
        {!isBalanced && (
          <div className="flex justify-between text-sm pt-1 border-t">
            <span className="text-gray-600">{remaining > 0 ? 'Remaining' : 'Excess'}</span>
            <span className={`font-semibold ${remaining > 0 ? 'text-red-600' : 'text-amber-600'}`}>
              ₹{Math.abs(remaining).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
